/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { BellRing } from 'lucide-react';
import { useCallback, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { type FollowUp } from '../../lib/api/follow-ups.js';
import { useFollowUpAlerts } from '../../lib/queries/follow-ups.js';

const EMPTY = { open: 0, overdue: 0, urgent: 0, items: [] as readonly FollowUp[] };

function AlertItem({ item }: { readonly item: FollowUp }): JSX.Element {
  const { t } = useTranslation();
  return (
    <li className="flex items-center justify-between rounded-xl border border-slate-100 px-3 py-2 text-sm dark:border-slate-800">
      <span className="min-w-0 truncate text-slate-700 dark:text-slate-200">{item.title}</span>
      {item.overdue && (
        <span className="ml-2 shrink-0 rounded-full bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-600 dark:bg-rose-500/10">
          {t('followUps.overdue')}
        </span>
      )}
    </li>
  );
}

/** Dashboard "Follow-up Alerts" panel (Figma admin dashboard). */
export function FollowUpAlertsCard(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const alerts = useFollowUpAlerts();
  const { open, overdue, urgent, items } = alerts.data ?? EMPTY;
  const goAll = useCallback(() => {
    void navigate('/admin/follow-ups');
  }, [navigate]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-semibold text-slate-800 dark:text-white">
          <BellRing className="h-5 w-5 text-[var(--accent)]" aria-hidden="true" />
          {t('followUps.alerts.title')}
        </h3>
        <button type="button" onClick={goAll} className="text-sm font-medium text-[var(--accent)]">
          {t('followUps.alerts.viewAll')}
        </button>
      </div>

      <div className="mb-4 grid grid-cols-3 gap-3">
        <Stat
          label={t('followUps.alerts.open')}
          value={open}
          tone="text-slate-800 dark:text-white"
        />
        <Stat label={t('followUps.alerts.overdue')} value={overdue} tone="text-rose-600" />
        <Stat label={t('followUps.alerts.urgent')} value={urgent} tone="text-amber-600" />
      </div>

      <ul className="space-y-2">
        {items.map((item) => (
          <AlertItem key={item.id} item={item} />
        ))}
        {items.length === 0 && (
          <li className="rounded-xl px-3 py-4 text-center text-sm text-slate-500 dark:text-slate-400">
            {t('followUps.alerts.none')}
          </li>
        )}
      </ul>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  readonly label: string;
  readonly value: number;
  readonly tone: string;
}): JSX.Element {
  return (
    <div className="rounded-xl bg-slate-50 p-3 text-center dark:bg-slate-800">
      <p className={`text-2xl font-bold ${tone}`}>{value}</p>
      <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
    </div>
  );
}
